import React from 'react';

interface DeckSlideWrapperProps {
  slideNumber: number;
  totalSlides: number;
  category: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

export const DeckSlideWrapper: React.FC<DeckSlideWrapperProps> = ({
  slideNumber,
  totalSlides,
  category,
  title,
  subtitle,
  children,
}) => {
  const formattedNumber = slideNumber.toString().padStart(2, '0');
  const formattedTotal = totalSlides.toString().padStart(2, '0');

  return (
    <div className="relative w-full h-full flex flex-col justify-between p-4 sm:p-7 lg:p-10 overflow-y-auto sm:overflow-hidden select-text">
      {/* Background ambient accents */}
      <div className="absolute top-0 right-0 w-80 sm:w-96 h-80 sm:h-96 bg-gold-500/5 dark:bg-gold-500/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />
      <div className="absolute bottom-0 left-0 w-80 sm:w-96 h-80 sm:h-96 bg-blue-500/5 dark:bg-blue-600/10 rounded-full blur-3xl pointer-events-none -ml-20 -mb-20" />

      {/* Slide Header */}
      <div className="relative z-10 flex items-start justify-between gap-3 border-b border-slate-200 dark:border-navy-700/60 pb-3 sm:pb-4">
        <div className="flex flex-col gap-1 max-w-4xl">
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <span className="inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-[11px] sm:text-xs font-mono font-medium tracking-wide uppercase bg-gold-500/15 dark:bg-gold-400/15 text-gold-800 dark:text-gold-400 border border-gold-500/30">
              {category}
            </span>
            <span className="text-[10px] sm:text-xs font-mono text-slate-500 dark:text-slate-400">
              WORKOUSE AI // CONFIDENTIAL PITCH DECK
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-slate-900 dark:text-white mt-1 leading-snug">
            {title}
          </h2>
          {subtitle && (
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 font-normal leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>

        {/* Slide Counter */}
        <div className="hidden sm:flex flex-col items-end shrink-0 pl-2">
          <div className="font-mono text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 dark:text-white tracking-tighter">
            <span className="text-gold-600 dark:text-gold-400">{formattedNumber}</span>
            <span className="text-slate-400 dark:text-slate-600 font-light text-sm sm:text-base"> / {formattedTotal}</span>
          </div>
        </div>
      </div>

      {/* Slide Body */}
      <div className="relative z-10 flex-1 my-auto py-3 sm:py-5 flex flex-col justify-center min-h-0">
        {children}
      </div>

      {/* Slide Footer */}
      <div className="relative z-10 flex items-center justify-between border-t border-slate-200 dark:border-navy-700/60 pt-2.5 sm:pt-3 text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 font-mono">
        <div className="flex items-center gap-1.5 sm:gap-2">
          <div className="w-2 h-2 rounded-full bg-gold-500 animate-pulse shrink-0" />
          <span className="truncate">Workouse AI Gateway — Zero-Trust Sovereignty</span>
        </div>
        <div className="flex items-center gap-3 sm:gap-4 shrink-0">
          <span className="hidden xs:inline">ai-gateway.workouse.com</span>
          <span className="hidden md:inline text-slate-300 dark:text-slate-700">|</span>
          <span className="hidden md:inline">Seed Round Deck</span>
        </div>
      </div>
    </div>
  );
};
